import { motion } from 'framer-motion';
import { Users, Award, Globe, BookOpen } from 'lucide-react';
import { companyInfo } from '../../data/mockData';

const AboutSection = () => {
  const features = [
    {
      icon: Users,
      title: 'Multidisciplinary Team',
      description: 'Engineers, economists, and PhD researchers working in sync'
    },
    {
      icon: Award,
      title: 'Institutional Heritage',
      description: 'Built on decades of TSO and Power Exchange leadership experience'
    },
    {
      icon: Globe,
      title: 'Global Expertise',
      description: 'International academic backgrounds applied to local markets'
    },
    {
      icon: BookOpen,
      title: 'Continuous Learning',
      description: 'New technologies, new techniques, new grid issues—everything changes. We keep learning, just like our models.'
    }
  ];

  return (
    <section id="about" className="py-20 lg:py-32 bg-primary-bg">
      <div className="container mx-auto px-6">
        
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 lg:mb-24"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-6xl font-display font-bold text-white mb-6">
            About Us
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            {companyInfo.description}
          </p>
        </motion.div>

        {/* Company Story */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {/* Left Column - Story */}
          <div className="space-y-6">
            <h3 className="text-3xl font-display font-bold text-white mb-6">
              Our Story
            </h3>
            
            {/* PARAGRAPH 1: Executive Foundation (Bertalan context) */}
            <p className="text-gray-300 leading-relaxed mb-4">
              Marvin AI acts as a bridge between institutional stability and modern technological disruption. 
              Founded by former senior executives from the region's Transmission System Operators (TSO) 
              and Power Exchanges, our company is built on over two decades of deep operational knowledge 
              within the energy sector. We don't just model data; we understand the grid.
            </p>

            {/* PARAGRAPH 2: Multidisciplinary Team (Márton & Mixed Engineering context) */}
            <p className="text-gray-300 leading-relaxed mb-4">
              Our strength lies in our diversity. We unite electrical and Machine Learning engineers with 
              economists and Power Market specialists. By combining the rigor of institutional financial assurance 
              and transaction advisory  with advanced technical modeling, we deliver solutions that are 
              not only mathematically sound but also financially viable and risk-aware.
            </p>

            {/* PARAGRAPH 3: Scientific/R&D Edge (Koronka context) */}
            <p className="text-gray-300 leading-relaxed">
              Innovation is in our DNA. Backed by PhD-level research and scientific achievements, 
              our team integrates classical energy optimization with the most up-to-date machine learning techniques. 
              With academic and professional backgrounds spanning multiple countries, 
              we translate complex international R&D findings into actionable forecasting tools for our clients.
            </p>
          </div>

          {/* Right Column - Key Facts */}
          <div className="space-y-6">
            <h3 className="text-3xl font-display font-bold text-white mb-6">
              Key Facts
            </h3>
            <div className="space-y-4">
              {[
                { label: 'Founded', value: companyInfo.foundedYear },
                { label: 'Team Size', value: '2-5' },
                { label: 'Headquarters', value: companyInfo.headquarters },
                { label: 'Experience', value: '20+ Years in Energy' },
                { label: 'R&D Focus', value: 'Grid Dynamics, automation and big data' }
              ].map((fact, index) => (
                <motion.div
                  key={index}
                  className="flex justify-between items-center py-3 border-b border-white/10"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <span className="text-gray-400">{fact.label}</span>
                  <span className="text-white font-semibold">{fact.value}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="text-center glass-morphism rounded-2xl p-6 bg-white/5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="mb-4 flex justify-center">
                <feature.icon size={48} className="text-primary-cyan" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">
                {feature.title}
              </h4>
              <p className="text-sm text-gray-400">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default AboutSection;